import { uuid } from "./uuid";

const BASE_DELAY = 1000;
const MAX_DELAY = 30000;
const MAX_SERVER_ERRORS = 5;
const MAX_AGE = 24 * 60 * 60 * 1000;

// Requests still out when their outbox is destroyed, by mutation id. A board
// reopened in the same page restores those changes from storage, and must wait
// for the first request's answer instead of sending the change twice.
const sharedInFlight = new Map();

export function createOutbox({
	state,
	fetch = (input, init) => globalThis.fetch(input, init),
	csrfToken = metaCsrfToken,
	storage = defaultStorage(),
	now = Date.now,
	setTimeout = globalThis.setTimeout,
	clearTimeout = globalThis.clearTimeout,
	onReject = (_rejection) => {},
	onReplay = (_replay) => {},
	onSettle = (_settle) => {},
	storageKey = sceneStorageKey(state),
	inFlight = sharedInFlight,
	// Whether the viewer may write right now. A queue it holds back stays stored and unsent, to resume if the right returns.
	canSend = () => true,
}) {
	const queue = [];
	let sending = false;
	let failures = 0;
	let timer = null;
	let destroyed = false;

	// `quiet` rides along to the refusal, for a caller that toasts none for it.
	function enqueue({ method = "POST", url, body = {}, ops = [], mutationId = uuid(), quiet = false }) {
		state.addPending({ mutationId, ops });
		queue.push({ mutationId, method, url, body, ops, queuedAt: now(), ...(quiet ? { quiet } : {}) });
		persist();
		send();
		return mutationId;
	}

	function restore() {
		const known = new Set(queue.map((entry) => entry.mutationId));
		for (const entry of read()) {
			if (known.has(entry.mutationId) || expired(entry)) continue;
			state.addPending({ mutationId: entry.mutationId, ops: entry.ops });
			queue.push(entry);
		}
		persist();
		send();
	}

	function flush() {
		if (destroyed) return;
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}
		failures = 0;
		send();
	}

	async function send() {
		if (destroyed || sending || timer || !canSend()) return;
		while (queue.length && expired(queue[0])) {
			reject(queue[0], null, null);
			queue.shift();
			persist();
		}
		if (queue.length === 0) return;
		sending = true;
		const entry = queue[0];
		const answer = await (inFlight.get(entry.mutationId) ?? attempt(entry));
		if (destroyed) return forgetSettled(entry, answer);
		const done = answer ? settle(entry, answer) : false;
		sending = false;
		if (done) {
			queue.shift();
			persist();
			send();
		} else {
			const delay = Math.min(BASE_DELAY * 2 ** failures, MAX_DELAY);
			failures += 1;
			timer = setTimeout(() => {
				timer = null;
				send();
			}, delay);
		}
	}

	function attempt(entry) {
		const answer = fetch(entry.url, request(entry)).then(
			async (response) => ({ ok: response.ok, status: response.status, payload: await parseJson(response) }),
			() => null,
		);
		inFlight.set(entry.mutationId, answer);
		answer.then(() => {
			if (inFlight.get(entry.mutationId) === answer) inFlight.delete(entry.mutationId);
		});
		return answer;
	}

	// An answer reaching a destroyed outbox touches no state; a final one only
	// takes its change out of storage, so no later board sends it again.
	function forgetSettled(entry, answer) {
		if (!storageKey || !answer || transient(answer.status)) return;
		const entries = read().filter((stored) => stored.mutationId !== entry.mutationId);
		try {
			if (entries.length) storage?.setItem(storageKey, JSON.stringify(entries));
			else storage?.removeItem(storageKey);
		} catch {}
	}

	function request(entry) {
		return {
			method: entry.method,
			credentials: "same-origin",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				"X-CSRF-Token": typeof csrfToken === "function" ? csrfToken() : csrfToken,
			},
			body: JSON.stringify({ ...entry.body, mutation_id: entry.mutationId }),
		};
	}

	function settle(entry, { ok, status, payload }) {
		if (ok) {
			failures = 0;
			const applied = confirm(entry.mutationId, payload);
			onSettle({ mutationId: entry.mutationId, applied, payload });
			return true;
		}
		if (transient(status)) {
			entry.serverErrors = (entry.serverErrors ?? 0) + 1;
			if (entry.serverErrors < MAX_SERVER_ERRORS) return false;
		}
		reject(entry, status, payload?.error ?? null);
		return true;
	}

	function reject(entry, status, reason) {
		state.dropPending(entry.mutationId);
		const dropped = dropDependents(entry);
		onReject({ mutationId: entry.mutationId, status, reason, quiet: Boolean(entry.quiet), dropped });
	}

	// The server keeps a mutation id for less than a retry could outlive, so a
	// change this old is given up rather than risk applying it twice.
	function expired(entry) {
		return now() - entry.queuedAt > MAX_AGE;
	}

	// Changes queued behind a refused create would each be refused too, for a
	// record that never existed: they go quietly, and one toast says why. Only an
	// upsert with an id creates; a singleton upsert (the sheet) has none to depend on.
	function dropDependents(refused) {
		if (refused.method !== "POST") return [];
		const created = new Set(refused.ops.filter((op) => op.op === "upsert" && op.id != null).map((op) => `${op.collection}:${op.id}`));
		const dropped = [];
		for (let index = queue.length - 1; index > 0; index--) {
			const entry = queue[index];
			if (!entry.ops.some((op) => created.has(`${op.collection}:${op.id}`))) continue;
			queue.splice(index, 1);
			state.dropPending(entry.mutationId);
			dropped.unshift(entry.mutationId);
		}
		return dropped;
	}

	function destroy() {
		destroyed = true;
		if (timer) clearTimeout(timer);
		timer = null;
	}

	// The broadcast echo normally settles a change. A response carrying the
	// diff settles it at once, but only when it is the very next version. A
	// response without one answers a replay: the change landed on an earlier
	// attempt whose echo may be gone, so only a resync can settle it. Says whether the
	// sheet now shows the write: false for a replay and for a version gap.
	function confirm(mutationId, payload) {
		const current = state.version.peek();
		if (Number.isInteger(payload?.version) && Array.isArray(payload.ops)) {
			if (payload.version === current + 1) state.applyDiff({ version: payload.version, ops: payload.ops, mutationId });
			else if (payload.version <= current) state.resolvePending(mutationId);
			else {
				state.acknowledge(mutationId);
				return false;
			}
			return true;
		}
		state.acknowledge(mutationId);
		onReplay({ mutationId });
		return false;
	}

	function persist() {
		if (!storageKey) return;
		try {
			if (queue.length) storage?.setItem(storageKey, JSON.stringify(queue));
			else storage?.removeItem(storageKey);
		} catch {}
	}

	function read() {
		if (!storageKey) return [];
		try {
			const entries = JSON.parse(storage?.getItem(storageKey) ?? "[]");
			return Array.isArray(entries) ? entries : [];
		} catch {
			return [];
		}
	}

	return {
		enqueue,
		restore,
		flush,
		destroy,
		get size() {
			return queue.length;
		},
	};
}

function transient(status) {
	return status === 408 || status === 429 || status >= 500;
}

// Without a scene id there is no key to keep changes apart, so nothing persists.
function sceneStorageKey(state) {
	const id = state.confirmed.peek().scene?.id;
	return id ? `live-outbox:${id}` : null;
}

// For a view that must never write (the table link): the same interface,
// with nothing applied, sent or stored.
export function readOnlyOutbox() {
	return { enqueue() {}, flush() {}, restore() {}, destroy() {} };
}

async function parseJson(response) {
	try {
		return await response.json();
	} catch {
		return null;
	}
}

function metaCsrfToken() {
	return /** @type {HTMLMetaElement | null} */ (globalThis.document?.querySelector('meta[name="csrf-token"]'))?.content;
}

function defaultStorage() {
	try {
		return globalThis.sessionStorage;
	} catch {
		return undefined;
	}
}
